import { ScriptLoader } from '../utils/script-loader';
import { createDeferred, Deferred } from '../utils';
import { Flavor } from '@uploadcam/common';

/**
 * Load Google API (Gapi) `<script>` element.
 *
 * This ensures that the `gapi.*` object will be available.
 */
export class GoogleApiService {
  private readonly gapiScript = new ScriptLoader('https://apis.google.com/js/api.js');

  private isGapiInitedDeferred?: Deferred<void>;
  private isPickerInitedDeferred?: Deferred<void>;

  constructor(
    private readonly apiKey: string,
    private readonly discoveryDocs: string[],
    private readonly flavor: Flavor,
  ) {}

  public isLoaded(): boolean {
    return this.gapiScript.isLoaded();
  }

  public async load(): Promise<void> {
    await this.gapiScript.load();

    if (!this.isGapiInitedDeferred && !this.isPickerInitedDeferred) {
      this.isGapiInitedDeferred = createDeferred();
      this.isPickerInitedDeferred = createDeferred();

      gapi.load('picker', () => this.isPickerInitedDeferred!.resolve());

      gapi.load('client', async () => {
        await gapi.client.init({
          apiKey: this.apiKey,
          discoveryDocs: this.discoveryDocs,
        });

        this.isGapiInitedDeferred!.resolve();
      });
    }

    await Promise.all([this.isGapiInitedDeferred!.promise, this.isPickerInitedDeferred!.promise]);
  }

  public async showFolderPicker(
    accessToken: string,
    title: string,
  ): Promise<google.picker.DocumentObject | undefined> {
    await this.load();

    const callback = createDeferred<google.picker.DocumentObject | undefined>();
    const baseView = () =>
      new google.picker.DocsView()
        .setIncludeFolders(true)
        .setMimeTypes('application/vnd.google-apps.folder')
        .setSelectFolderEnabled(true);

    const myDriveView = baseView().setParent('root').setOwnedByMe(true);
    const sharedDrivesView = baseView().setEnableDrives(true);
    const sharedWithMeView = baseView().setOwnedByMe(false);

    // Show only "Shared Drives" for SchoolCam, and all views for UploadCam
    const basePicker =
      this.flavor === 'sc'
        ? new google.picker.PickerBuilder().addView(sharedDrivesView)
        : new google.picker.PickerBuilder()
            .addView(myDriveView)
            .addView(sharedDrivesView)
            .addView(sharedWithMeView);

    const picker = basePicker
      .setTitle(title)
      .setOAuthToken(accessToken)
      .setDeveloperKey(this.apiKey)
      .setCallback((data) => {
        switch (data.action) {
          case 'cancel':
            callback.resolve(undefined);
            break;

          case 'picked':
            if (data.docs?.length) {
              callback.resolve(data.docs[0]);
            } else {
              callback.resolve(undefined);
            }
            break;
        }
      })
      .build();

    picker.setVisible(true);

    return callback.promise;
  }
}
